import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Trash2 } from 'lucide-react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useIntroNote } from '@/src/renderer/lib/intro'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { SQUIRCLE_PATH } from '@/src/renderer/components/ui/RewardCatalogIcon/squircle'
import { Button } from '@/src/renderer/components/ui/Button'
import { MotivationIcon } from '@/src/renderer/components/icons'
import {
  filterRewards,
  groupRewardsByCategory,
  getRewardById,
  rewardCta,
  REWARD_CATEGORIES,
  type CatalogLanguage,
  type CatalogReward,
} from '@/src/renderer/lib/data/rewardCatalog'
import { RewardCategoryFilter } from './RewardCategoryFilter'
import type { RewardPickerContentProps } from './RewardPicker.types'
import './RewardPicker.scss'

const RECENT_LIMIT = 5

/** Synced preference key — see `DataSource.usePreference`. */
const CATEGORY_FILTER_KEY = 'rewardPicker.categoryIds'
/** Stable empty array so the memo below doesn't churn on every render. */
const EMPTY_CATEGORY_FILTER: string[] = []

interface RewardSection {
  /** Stable key — a category id, or `recent` for the "Zuletzt benutzt" group. */
  key: string
  label: string
  rewards: CatalogReward[]
}

/**
 * Reward picker (Figma 3332:49448) — the popover behind "Belohnung hinzufügen".
 *
 * Reads the bundled catalogue only (ADR 0003), so it works offline and for
 * users without HappyCloud. Selection is local until confirmed: clicking a row
 * marks it, "Bestätigen" (or Enter) hands it to the task.
 *
 * "Zuletzt benutzt" appears only in the unfiltered view — once the user
 * searches or picks categories, the list is theirs to scan (matches the two
 * Figma states).
 */
export const RewardPickerContent: React.FC<RewardPickerContentProps> = ({
  value,
  onValueChange,
  onClose,
  removable = !!value,
  placement = 'actionbar',
}) => {
  const { t, i18n } = useTranslation()
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'
  const inline = placement === 'inline'

  const source = useDataSource()
  const noteIntro = useIntroNote()
  const [query, setQuery] = useState('')
  // Survives closing the picker AND follows the user to their other devices.
  // Ids of categories that no longer exist are dropped: the catalogue can
  // change with an app update, and a dead id would filter the list to nothing
  // with no way for the user to see why.
  const [storedCategoryIds, setStoredCategoryIds, filterReady] =
    source.usePreference<string[]>(CATEGORY_FILTER_KEY, EMPTY_CATEGORY_FILTER)
  const categoryIds = useMemo(
    () => (filterReady ? storedCategoryIds.filter(id => REWARD_CATEGORIES.some(c => c.id === id)) : EMPTY_CATEGORY_FILTER),
    [storedCategoryIds, filterReady],
  )
  const [selectedId, setSelectedId] = useState<string | null>(value)
  const [recentIds, setRecentIds] = useState<string[]>([])
  const listRef = useRef<HTMLDivElement>(null)

  // Recency is read once — the picker is short-lived and the list must not
  // reshuffle under the user's cursor while they browse.
  useEffect(() => {
    let cancelled = false
    source.getRecentRewardUsages(RECENT_LIMIT)
      .then(usages => {
        if (!cancelled) setRecentIds(usages.map(u => u.rewardId))
      })
      .catch(() => { /* empty history is a fine fallback */ })
    return () => { cancelled = true }
  }, [source])

  const isFiltered = query.trim().length > 0 || categoryIds.length > 0

  const sections = useMemo<RewardSection[]>(() => {
    const matches = filterRewards({ query, categoryIds })
    const groups = groupRewardsByCategory(matches).map(group => ({
      key: group.category.id,
      label: language === 'en' ? group.category.nameEn : group.category.nameDe,
      rewards: group.rewards,
    }))

    if (isFiltered) return groups

    const recent = recentIds.flatMap(id => {
      const reward = getRewardById(id)
      return reward ? [reward] : []
    })
    return recent.length > 0
      ? [{ key: 'recent', label: t('taskCreation.rewardPicker.recent'), rewards: recent }, ...groups]
      : groups
  }, [query, categoryIds, isFiltered, recentIds, language, t])

  /** Flat order of the visible rows — drives arrow-key navigation. */
  const flatIds = useMemo(
    () => sections.flatMap(section => section.rewards.map(reward => `${section.key}:${reward.id}`)),
    [sections],
  )

  const handleConfirm = useCallback(() => {
    if (!selectedId) return
    // Die eine Auswahl hinter allen Wegen zur Belohnung — hier zählt „Entdecken › Belohnung“ (#323).
    noteIntro('reward')
    if (inline) { onValueChange(selectedId); return }
    onClose()
    // Same deferral as the tag dropdown: let the popover close before the
    // parent re-renders the task row.
    setTimeout(() => onValueChange(selectedId), 100)
  }, [inline, selectedId, onValueChange, onClose, noteIntro])

  /**
   * Detaching follows `value` — the reward the TASK carries — not `selectedId`,
   * which is only a candidate until the user confirms it. Marking a different
   * row and then hitting the trash still removes what is actually attached.
   *
   * No `inline` branch here: the trash button that calls this is never
   * rendered in `inline` (see the footer below — no Papierkorb-Fuß there), so
   * this path only ever runs in `actionbar`/`badge`, where the deferral still
   * applies.
   */
  const handleDetach = useCallback(() => {
    onClose()
    setTimeout(() => onValueChange(null), 100)
  }, [onValueChange, onClose])

  const move = useCallback((delta: number) => {
    if (flatIds.length === 0) return
    const current = flatIds.findIndex(key => key.endsWith(`:${selectedId}`))
    const next = current < 0
      ? (delta > 0 ? 0 : flatIds.length - 1)
      : Math.max(0, Math.min(flatIds.length - 1, current + delta))
    const nextId = flatIds[next]?.split(':').slice(1).join(':')
    if (nextId) setSelectedId(nextId)
  }, [flatIds, selectedId])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      move(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      move(-1)
    } else if (e.key === 'Enter') {
      // A focused footer button acts on Enter by itself — confirming here too
      // would run the whole commit twice.
      if ((e.target as HTMLElement).closest('.reward-picker__footer')) return
      e.preventDefault()
      handleConfirm()
    }
    // Escape is left to the popover — Radix closes on it.
  }, [move, handleConfirm])

  // Keep the marked row visible while arrowing through the deck.
  useEffect(() => {
    if (!selectedId) return
    listRef.current
      ?.querySelector(`[data-reward-id="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [selectedId])

  return (
    // Key handling sits on the wrapper so ↑/↓/⏎ work from the search field and
    // from the list alike — same approach as the other task dropdowns.
    <div className="reward-picker" data-placement={placement} onKeyDown={handleKeyDown}>
      <header className="reward-picker__header">
        <span className="reward-picker__header-icon" aria-hidden="true">
          {/* Same squircle the reward icons in the list use, so the header
              reads as one of them rather than a plain rounded box. */}
          <svg
            className="reward-picker__header-squircle"
            width="32"
            height="32"
            viewBox="0 0 40 40"
            fill="none"
          >
            <path d={SQUIRCLE_PATH} />
          </svg>
          <MotivationIcon size={16} className="reward-picker__header-glyph" />
        </span>
        <div className="reward-picker__header-text">
          <p className="reward-picker__title">
            {t('taskCreation.rewardPicker.title')}
            <span className="reward-picker__optional"> {t('taskCreation.rewardPicker.optional')}</span>
          </p>
          <p className="reward-picker__subtitle">{t('taskCreation.rewardPicker.subtitle')}</p>
        </div>
      </header>

      <div className="reward-picker__search-row">
        <label className="reward-picker__search">
          <Search size={16} className="reward-picker__search-icon" />
          <input
            className="reward-picker__input"
            value={query}
            placeholder={t('taskCreation.rewardPicker.searchPlaceholder')}
            onChange={e => setQuery(e.target.value)}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
          />
        </label>
        <RewardCategoryFilter value={categoryIds} onValueChange={setStoredCategoryIds} />
      </div>

      <div className="reward-picker__list" ref={listRef}>
        {sections.length === 0 && (
          <p className="reward-picker__empty">{t('taskCreation.rewardPicker.empty')}</p>
        )}
        {sections.map(section => (
          <section key={section.key} className="reward-picker__group">
            <h3 className="reward-picker__group-label">{section.label}</h3>
            {section.rewards.map(reward => (
              <button
                key={`${section.key}:${reward.id}`}
                type="button"
                data-reward-id={reward.id}
                className={`reward-picker__item${reward.id === selectedId ? ' reward-picker__item--selected' : ''}`}
                onClick={() => setSelectedId(reward.id)}
                onDoubleClick={handleConfirm}
              >
                <RewardCatalogIcon rewardId={reward.id} name="" size={32} />
                <span className="reward-picker__item-name">{rewardCta(reward, language)}</span>
              </button>
            ))}
          </section>
        ))}
      </div>

      {/* Same footer trio as the when / tag dropdowns: secondary, then the
          outline icon button for the destructive action right next to the
          primary (Sven, 10.10.2026), all on the right. */}
      <footer className="reward-picker__footer">
        <div className="reward-picker__footer-actions">
          {/* Placeholder until the "eigene Belohnungen" flow lands — disabled
              rather than silently inert, so nobody clicks into a void. */}
          <Button
            variant="secondary"
            shortcut={{ kind: 'character', text: 'A' }}
            disabled
            title={t('taskCreation.rewardPicker.comingSoon')}
          >
            {t('taskCreation.rewardPicker.addCustom')}
          </Button>
          {/* Since the reward trigger dropped its remove-X, this is the only way
              to detach a reward — so it is bound to the attached one, not to the
              pending highlight. In `inline`, removal happens in the area itself,
              not via a trash foot (that belongs to `badge`). */}
          {removable && !inline && (
            <Button
              variant="outline"
              icon={<Trash2 size={16} />}
              onClick={handleDetach}
              title={t('taskCreation.rewardPicker.remove')}
              aria-label={t('taskCreation.rewardPicker.remove')}
            />
          )}
          {selectedId ? (
            <Button variant="primary" shortcut={{ kind: 'return' }} onClick={handleConfirm}>
              {t('taskCreation.rewardPicker.confirm')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              shortcut={{ kind: 'character', text: 'ESC' }}
              onClick={onClose}
            >
              {t('taskCreation.rewardPicker.close')}
            </Button>
          )}
        </div>
      </footer>
    </div>
  )
}
